/**
 * Options for configuring how browser locales are retrieved and formatted
 */
export interface BrowserLocaleOptions {
    /**
     * If true, returns only the language code portion of the locale (e.g., 'en' instead of 'en-US')
     * @default false
     */
    languageCodeOnly?: boolean;

    /**
     * Default locale to use when browser locale detection fails or is unavailable
     * @default "en-US"
     */
    defaultLocale?: string;
}

/**
 * Retrieves browser locales from the navigator object
 * @param options Configuration options for locale retrieval
 * @returns Array of locale strings, or undefined if no locales are available
 *
 * @example
 * ```typescript
 * // Get full locale codes
 * const locales = getBrowserLocales(); // ['en-US', 'es-ES', 'fr-FR']
 *
 * // Get only language codes
 * const languages = getBrowserLocales({ languageCodeOnly: true }); // ['en', 'es', 'fr']
 *
 * // Use custom default locale
 * const localesWithDefault = getBrowserLocales({ defaultLocale: "fr-FR" }); // Falls back to 'fr-FR'
 * ```
 */
export function getBrowserLocales(options: BrowserLocaleOptions = {}): string[] | undefined {
    const defaultOptions: Required<BrowserLocaleOptions> = {
        languageCodeOnly: false,
        defaultLocale: "en-US"
    };

    const opt: Required<BrowserLocaleOptions> = {
        ...defaultOptions,
        ...options
    };

    // Handle browser compatibility: some browsers may not support navigator.languages
    const browserLocales: readonly string[] | undefined = window.navigator.languages === undefined ? (window.navigator.language ? [window.navigator.language] : undefined) : window.navigator.languages;

    if (!browserLocales) {
        // Return default locale as fallback
        const fallbackLocale = opt.languageCodeOnly ? opt.defaultLocale.split(/[-_]/)[0] || opt.defaultLocale : opt.defaultLocale;
        return [fallbackLocale];
    }

    return browserLocales.map((locale: string): string => {
        const trimmedLocale = locale.trim();

        return opt.languageCodeOnly
            ? trimmedLocale.split(/[-_]/)[0] || trimmedLocale // Fallback to full locale if split fails
            : trimmedLocale;
    });
}

/**
 * Gets the primary browser locale
 * @param options Configuration options for locale retrieval
 * @returns The primary locale string, uses defaultLocale if no browser locales are available
 */
export function getPrimaryBrowserLocale(options: BrowserLocaleOptions = {}): string {
    const defaultOptions: Required<BrowserLocaleOptions> = {
        languageCodeOnly: false,
        defaultLocale: "en-US"
    };

    const opt: Required<BrowserLocaleOptions> = {
        ...defaultOptions,
        ...options
    };

    const locales = getBrowserLocales(options);

    // Always return a locale - either the first detected one or the default
    return locales?.[0] || opt.defaultLocale;
}

/**
 * Checks if a specific locale is supported by the browser
 * @param targetLocale The locale to check for support
 * @param options Configuration options for locale comparison
 * @returns True if the locale is supported, false otherwise
 */
export function isBrowserLocaleSupported(targetLocale: string, options: BrowserLocaleOptions = {}): boolean {
    const locales = getBrowserLocales(options);
    if (!locales) {
        return false;
    }

    const normalizedTarget = options.languageCodeOnly ? targetLocale.split(/[-_]/)[0] : targetLocale;

    return locales.includes(normalizedTarget);
}

/**
 * Type guard to check if the navigator object supports locale detection
 * @returns True if locale detection is supported
 */
export function isLocaleDetectionSupported(): boolean {
    return typeof window.navigator !== "undefined" && (typeof window.navigator.language === "string" || Array.isArray(window.navigator.languages));
}
